import { useState, useEffect, useCallback } from 'react';
import { Phone, PhoneCall, Clock, AlertTriangle, CheckCircle, Copy, RefreshCw, UserCheck, Zap } from 'lucide-react';
import api from '@/lib/api';
import VoiceCallCard from '../components/VoiceCallCard';

const TABS    = ['Live Calls', 'Scheduled Calls', 'Generate PIN'];
const POLL_MS = 8000;

const statusBadge = (status) => {
  const map = {
    active:      'bg-emerald-100 text-emerald-700 border-emerald-200',
    in_progress: 'bg-blue-100 text-blue-700 border-blue-200',
    pending:     'bg-amber-100 text-amber-700 border-amber-200',
    handoff:     'bg-purple-100 text-purple-700 border-purple-200',
    completed:   'bg-slate-100 text-slate-600 border-slate-200',
    failed:      'bg-red-100 text-red-700 border-red-200',
  };
  return map[status] || 'bg-slate-100 text-slate-600 border-slate-200';
};

export default function VoiceDashboard() {
  const workspaceId     = localStorage.getItem('vocala_workspace_id') || '';
  const [activeTab,     setActiveTab]     = useState('Live Calls');
  const [liveCalls,     setLiveCalls]     = useState([]);
  const [scheduled,     setScheduled]     = useState([]);
  const [loading,       setLoading]       = useState(false);
  const [pinForm,       setPinForm]       = useState({ candidate_id: '', job_id: '', phone: '' });
  const [generatedPin,  setGeneratedPin]  = useState(null);
  const [pinCopied,     setPinCopied]     = useState(false);
  const [toast,         setToast]         = useState(null);
  const [metrics,       setMetrics]       = useState(null);
  const [metricsError,  setMetricsError]  = useState('');

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchLive = useCallback(async () => {
    try {
      const { data } = await api.get('/voice/live-calls', { params: { workspace_id: workspaceId } });
      setLiveCalls(Array.isArray(data) ? data : []);
    } catch (err) { logger.error('[VoiceDashboard] fetchLive failed:', err?.message); }
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchScheduled = useCallback(async () => {
    try {
      const { data } = await api.get('/voice/scheduled-calls', { params: { workspace_id: workspaceId } });
      setScheduled(Array.isArray(data) ? data : []);
    } catch (err) { logger.error('[VoiceDashboard] fetchScheduled failed:', err?.message); }
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchLive();
    fetchScheduled();
    const t = setInterval(() => { fetchLive(); fetchScheduled(); }, POLL_MS);
    return () => clearInterval(t);
  }, [fetchLive, fetchScheduled]);

  useEffect(() => {
    api.get('/voice/metrics', { params: { workspace_id: workspaceId } })
      .then(r => setMetrics(r.data))
      .catch(() => setMetricsError('Unable to load voice metrics.'));
  }, [workspaceId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleHandoff = async (callSessionId) => {
    try {
      await api.post(`/voice/calls/${callSessionId}/handoff`, null, { params: { workspace_id: workspaceId } });
      showToast('Human handoff triggered successfully.');
      fetchLive();
    } catch (e) {
      showToast(e?.response?.data?.detail || 'Handoff failed.', 'error');
    }
  };

  const handleResendPin = async (id) => {
    try {
      await api.post(`/voice/calls/${id}/resend-pin`, null, { params: { workspace_id: workspaceId } });
      showToast('PIN SMS resent successfully.');
    } catch {
      showToast('Failed to resend PIN.', 'error');
    }
  };

  const handleGeneratePin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post('/voice/generate-pin', null, {
        params: { workspace_id: workspaceId, ...pinForm },
      });
      setGeneratedPin(data);
      fetchScheduled();
      showToast(`PIN ${data.pin} generated — expires in 72 h.`);
    } catch (err) {
      showToast(err?.response?.data?.detail || 'PIN generation failed.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const copyPin = () => {
    if (!generatedPin) return;
    navigator.clipboard.writeText(generatedPin.pin);
    setPinCopied(true);
    setTimeout(() => setPinCopied(false), 2000);
  };

  return (
    <div className="flex-1 overflow-auto bg-transparent p-6 sm:p-8 relative" data-testid="voice-dashboard" style={{ fontFamily: 'Inter, sans-serif' }}>

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-4 rounded-2xl shadow-xl border bg-white/95 backdrop-blur-xl border-slate-200/60 min-w-[280px] animate-in slide-in-from-right-8 fade-in duration-300">
          {toast.type === 'error'
            ? <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
            : <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
          <p className="text-sm font-semibold text-slate-800">{toast.msg}</p>
        </div>
      )}

      {/* Header */}
      <div className="bg-white/90 backdrop-blur-xl border border-slate-200/60 rounded-3xl shadow-xl p-6 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 flex items-center justify-center shadow-lg">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent tracking-tight">
              Voice Screening
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">AI-powered phone screening · Telnyx + Deepgram Nova-3</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 border border-emerald-200 rounded-xl">
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="text-xs font-bold text-emerald-700">{liveCalls.length} Live</span>
          </div>
          <button onClick={() => { fetchLive(); fetchScheduled(); }}
            className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
            data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-white/80 backdrop-blur-sm border border-slate-200/60 rounded-2xl mb-6 w-fit shadow-sm">
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-5 py-2.5 rounded-xl text-sm font-bold transition-all ${
              activeTab === tab
                ? 'bg-white shadow-md text-blue-600 border border-slate-200/60'
                : 'text-slate-500 hover:text-slate-700'}`}
            data-testid={`tab-${tab.toLowerCase().replace(/ /g, '-')}`}
          >
            {tab}
            {tab === 'Live Calls' && liveCalls.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-emerald-500 text-white text-xs rounded-full">{liveCalls.length}</span>
            )}
            {tab === 'Scheduled Calls' && scheduled.length > 0 && (
              <span className="ml-2 px-1.5 py-0.5 bg-amber-500 text-white text-xs rounded-full">{scheduled.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── Live Calls ─────────────────────────────────────────── */}
      {activeTab === 'Live Calls' && (
        <div className="space-y-4">
          {liveCalls.length === 0 ? (
            <div className="bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl p-12 flex flex-col items-center shadow-sm">
              <PhoneCall className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-slate-500 font-semibold">No active calls right now</p>
              <p className="text-slate-400 text-sm mt-1">Active calls appear here in real time, auto-refreshing every 8 s</p>
            </div>
          ) : (
            liveCalls.map(call => (
              <VoiceCallCard key={call.call_session_id || call.callsessionid} call={call} onHandoff={handleHandoff} />
            ))
          )}
        </div>
      )}

      {/* ── Scheduled Calls ────────────────────────────────────── */}
      {activeTab === 'Scheduled Calls' && (
        <div>
          {/* Voice Metrics card */}
          {metrics && (
            <div className="mb-4 bg-white/80 backdrop-blur-sm border border-slate-200/50 rounded-2xl px-5 py-4 shadow-sm">
              <h3 className="text-sm font-bold text-slate-800 mb-3">Voice Metrics — Last {metrics.window_days} Days</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                {[
                  { label: 'Scheduled',  val: metrics.total_scheduled },
                  { label: 'Completed',  val: metrics.total_completed },
                  { label: '1st/2nd/3rd attempt', val: `${metrics.attempts.completed_on_1}/${metrics.attempts.completed_on_2}/${metrics.attempts.completed_on_3}` },
                  { label: 'Failed (max attempts)', val: `${metrics.total_failed} (${metrics.attempts.max_attempts_failed})` },
                ].map(({ label, val }) => (
                  <div key={label}>
                    <p className="text-base font-black text-slate-900">{val}</p>
                    <p className="text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          {metricsError && <p className="mb-3 text-xs text-red-500">{metricsError}</p>}

        <div className="bg-white/85 backdrop-blur-sm border border-slate-200/60 rounded-2xl shadow-lg overflow-hidden">
          {scheduled.length === 0 ? (
            <div className="p-12 flex flex-col items-center">
              <Clock className="w-10 h-10 text-slate-300 mb-3" />
              <p className="text-slate-500 font-semibold">No scheduled calls</p>
              <p className="text-slate-400 text-sm mt-1">Generate a PIN to schedule a candidate screening</p>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-slate-200/60">
              <thead className="bg-slate-50/90 sticky top-0 backdrop-blur-md">
                <tr>
                  {['Candidate Phone', 'Scheduled Time', 'PIN', 'Status', 'Attempts', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200/60 bg-transparent">
                {scheduled.map(call => (
                  <tr key={call.id} className="hover:bg-slate-50/80 transition-colors" data-testid={`scheduled-row-${call.id}`}>
                    <td className="px-4 py-4 align-middle text-sm font-semibold text-slate-900">
                      {call.phone}
                      {call.preferred_time_window && (
                        <div className="mt-0.5 text-[11px] text-slate-500 font-normal truncate max-w-[140px]" title={call.preferred_time_window}>
                          Prefers: {call.preferred_time_window}
                        </div>
                      )}
                      {call.candidate_notes?.length > 0 && (
                        <span className="mt-1 inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-bold text-amber-700 border border-amber-200">
                          Needs follow-up
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4 align-middle text-sm text-slate-600">{new Date(call.scheduled_time || call.scheduledtime).toLocaleString()}</td>
                    <td className="px-4 py-4 align-middle">
                      <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-slate-100 border border-slate-200 text-slate-800 font-mono font-bold text-sm tracking-widest">
                        {call.pin}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${statusBadge(call.status)}`}>
                        {call.status}
                      </span>
                    </td>
                    <td className="px-4 py-4 align-middle text-sm font-semibold text-slate-700">
                      {call.attempts ?? 0}/3
                    </td>
                    <td className="px-4 py-4 align-middle">
                      <button onClick={() => handleResendPin(call.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-blue-50 text-blue-700 hover:bg-blue-100 font-semibold text-sm transition-colors">
                        <Zap className="w-3.5 h-3.5" /> Resend SMS
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      )}

      {/* ── Generate PIN ───────────────────────────────────────── */}
      {activeTab === 'Generate PIN' && (
        <div className="max-w-lg">
          <div className="bg-white/95 backdrop-blur-xl border border-slate-200/60 rounded-3xl shadow-xl overflow-hidden">
            <div className="bg-gradient-to-r from-slate-50 to-white px-6 py-5 border-b border-slate-200/60">
              <h2 className="text-xl font-bold bg-gradient-to-r from-slate-900 to-slate-700 bg-clip-text text-transparent">
                Generate Screening PIN
              </h2>
              <p className="text-sm text-slate-500 mt-1">Creates a 6-digit PIN valid for 72 h and schedules the call</p>
            </div>
            <form onSubmit={handleGeneratePin} className="p-6 space-y-4">
              {[
                { label: 'Candidate ID', field: 'candidate_id', placeholder: 'UUID from candidates collection' },
                { label: 'Job ID',       field: 'job_id',       placeholder: 'UUID from jobs collection' },
                { label: 'Phone Number', field: 'phone',        placeholder: '+1 555 000 0000' },
              ].map(({ label, field, placeholder }) => (
                <div key={field}>
                  <label className="block text-sm font-bold text-slate-700 mb-2">{label}</label>
                  <input
                    type="text"
                    value={pinForm[field]}
                    onChange={e => setPinForm(p => ({ ...p, [field]: e.target.value }))}
                    placeholder={placeholder}
                    required
                    data-testid={`pin-form-${field}`}
                    className="w-full px-4 py-3.5 border border-slate-200/70 rounded-xl bg-slate-50/50 focus:ring-2 focus:ring-blue-500 outline-none text-slate-900 transition-all hover:bg-white shadow-sm"
                  />
                </div>
              ))}
              <button type="submit" disabled={loading} data-testid="generate-pin-btn"
                className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 text-white px-6 py-3 rounded-xl font-bold shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:hover:scale-100 flex items-center justify-center gap-2">
                {loading
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Generating...</>
                  : <><Zap className="w-4 h-4" /> Generate PIN</>
                }
              </button>
            </form>

            {generatedPin && (
              <div className="px-6 pb-6">
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5" data-testid="generated-pin-block">
                  <p className="text-xs font-bold text-emerald-600 uppercase tracking-wider mb-2">PIN Generated</p>
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-4xl font-black text-emerald-700 tracking-[0.3em] font-mono" data-testid="pin-display">
                      {generatedPin.pin}
                    </span>
                    <button onClick={copyPin} data-testid="copy-pin-btn"
                      className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold text-sm transition-all ${
                        pinCopied ? 'bg-emerald-500 text-white' : 'bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-50'}`}>
                      {pinCopied ? <><CheckCircle className="w-4 h-4" /> Copied!</> : <><Copy className="w-4 h-4" /> Copy PIN</>}
                    </button>
                  </div>
                  <p className="text-xs text-emerald-600 mt-2">
                    Expires: {new Date(generatedPin.expires_at || generatedPin.expiresat).toLocaleString()} · SMS sent to candidate
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
